// Copyright (c) Docugami, Inc. All rights reserved.

import { CanvasHelperStatics } from "components/doc_hosting/doc_hosts/web/CanvasHelperStatics";
import { DefaultWorldSpaceConfiguration } from "sdv/Space";
import { RenderBox } from "sdv/components/ChunkRenderComponent";
import { OverlayComponent } from "sdv/components/OverlayComponent";
import { ViewComponent } from "sdv/components/ViewComponent";
import { ComponentId } from "sdv/engine/Component";
import { IDrawCommand, IRenderer, IRendererInput, Queries } from "sdv/engine/Engine";
import { Box } from "store/shared_types";

/**
 * Renders overlays over the canvas
 */
export class OverlayRenderer implements IRenderer {
    get renderEnabled(): boolean { return true; }

    queries: Queries = {
        view: { types: [ComponentId.View] },
        overlays: { types: [ComponentId.Overlay] }
    }

    render(engine: IRendererInput): IDrawCommand[] {
        const overlays = this.queries.overlays.query!.entities;
        if (!overlays.length) {
            return [];
        }

        const view = this.queries.view.query!.single()?.getComponent<ViewComponent>(ComponentId.View);
        if (!view) {
            return [];
        }

        const viewTop = view.viewport.worldPosition.y;
        const viewBottom = viewTop + view.viewport.worldHeight;

        const commands: IDrawCommand[] = [];
        for (const overlay of overlays) {
            const overlayComp = overlay.getComponent<OverlayComponent>(ComponentId.Overlay)!;
            // Default overlay to cover full view
            let box = { left: 0, right: DefaultWorldSpaceConfiguration.width, top: viewTop, bottom: viewBottom, page: 0 };
            if (!!overlayComp?.overlayZone) {
                box = overlayComp.overlayZone;
            }

            commands.push(context => fillRectangleWithExclusions(context, box, overlayComp.color, overlayComp.boxesToExclude, overlayComp.opacity));
        }

        return commands;
    }
}

function fillRectangleWithExclusions(
    ctx: CanvasRenderingContext2D,
    rect: Box,
    color: string,
    excludedBoxes: RenderBox[] = [],
    alpha?: number): void {

    ctx.globalAlpha = alpha ?? 1.0;
    ctx.globalCompositeOperation = 'multiply';

    ctx.fillStyle = color;
    ctx.fillRect(0, 0, rect.right, rect.bottom);
    for (const renderBox of excludedBoxes) {
        ctx.clearRect(renderBox.box.left, renderBox.box.top, renderBox.box.right - renderBox.box.left, renderBox.box.bottom - renderBox.box.top);

        if (!!renderBox.color) {
            CanvasHelperStatics.fillRectangle(ctx, renderBox.box, renderBox.color, false);
        }

        if (!!renderBox.strokeColor) {
            CanvasHelperStatics.strokeRectangle(ctx, renderBox.box, renderBox.strokeColor, renderBox.strokeWidth ?? 1, false);
        }
    }

    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
}
